import ProcessFlowStep from "~/models/processFlows/processFlowStep"
import {ProcessFlowStepType} from '../../../enums/processFlowStepType'
import {IProcessFlowStepConstructorOptions, IProcessFlowVariable, ITakeActionStep} from '~/interfaces/processFlowInterfaces'
import ProcessFlowMappedVariable from '~/models/processFlows/steps/processFlowMappedVariable'
import {IWorkflowStep} from "../../../../formability/form_builder/models/workflow/workflowStep";
import {IWorkflowStepAction} from "~/interfaces/workflowStepInterface";
import {ReferencePathNode} from "~/models/referencePaths/referencePathNode";
import {IReferencePath, IReferencePathNodeForm, IReferencePathNodeProcessFlowVariable, IReferencePathNodeSpecific, IReferencePathNodeWorkflowStep, IReferencePathNodeWorkflowStepAction} from "~/interfaces/referencePathInterfaces";
import {ReferenceNodeType} from "~/enums/referenceNodeType";
import ProcessFlowTakeActionStepValidation from '~/models/navigableValidations/processFlowTakeActionStepValidation'
import {Logger} from "~/services/logger";

type TakeActionReferenceNodes = [
  IReferencePathNodeProcessFlowVariable,
  IReferencePathNodeForm,
  IReferencePathNodeWorkflowStep,
  IReferencePathNodeWorkflowStepAction,
]

interface ITakeActionStepPath extends IReferencePath {
  nodes: TakeActionReferenceNodes
}

type TakeActionNodePosition = 0 | 1 | 2 | 3

type TakeActionReferenceNodeType = ReferenceNodeType.ProcessFlowVariable |
  ReferenceNodeType.Form |
  ReferenceNodeType.WorkflowStep |
  ReferenceNodeType.WorkflowStepAction

type TakeActionStepMappedVariable =
  ProcessFlowMappedVariable
  & { source_variable_path: ITakeActionStepPath }

export class ProcessFlowTakeActionStep extends ProcessFlowStep implements ITakeActionStep {
  step_type: ProcessFlowStepType.TakeAction
  /**
   * +variable_reference.source_variable_path.nodes+ should look like the following when fully configured:
   *    nodes: [
   *      {reference_type: ReferenceNodeType.ProcessFlowVariable, ...},
   *      {reference_type: ReferenceNodeType.Form, ...},
   *      {reference_type: ReferenceNodeType.WorkflowStep, ...},
   *      {reference_type: ReferenceNodeType.WorkflowStepAction, ...},
   *    ]
   */
  variable_reference: TakeActionStepMappedVariable

  constructor(parent, options: IProcessFlowStepConstructorOptions) {
    super(parent, options)

    // do not setup defaults when loading json from backend
    // will result in hard-to track down overrides in change tracking
    if (options.firstCreation) {
      this.initDefaults()
    }
  }

  //******************** VALIDATABLE ********************//
  _validation: ProcessFlowTakeActionStepValidation = new ProcessFlowTakeActionStepValidation(this);

  //******************** VALIDATABLE END ********************//

  initDefaults() {
    let varRef = this.variable_reference
    if (!varRef) {
      varRef = new ProcessFlowMappedVariable('variable_reference') as TakeActionStepMappedVariable
      varRef.attachToParent(this)
    }
    varRef.initializeSourceVariablePath()
    this.initNodes()
  }

  get defaultTitle(): string {
    const name = this.formVariableNode?.reference()?.name || 'form'
    let title = 'Take Action'
    if (title) title += ` for ${name}`
    return title
  }

  get formVariableNode(): IReferencePathNodeProcessFlowVariable {
    return this.path?.nodes[0]
  }

  get formVariable(): IProcessFlowVariable {
    const node = this.formVariableNode
    if (!node) return null

    return node.service.reference()
  }

  get formNode(): IReferencePathNodeForm {
    return this.path?.nodes[1]
  }

  get workflowStepNode(): IReferencePathNodeWorkflowStep {
    return this.path?.nodes[2]
  }

  get workflowStep(): IWorkflowStep {
    const node = this.workflowStepNode
    if (!node) return null

    return node.service.reference()
  }

  get workflowStepActionNode(): IReferencePathNodeWorkflowStepAction {
    return this.path?.nodes[3]
  }

  get workflowStepAction(): IWorkflowStepAction {
    const node = this.workflowStepActionNode
    if (!node) return null

    return node.service.reference()
  }

  updateFormVariableNode(json: { reference_id: string }) {
    this.updateNode({...json, reference_type: ReferenceNodeType.ProcessFlowVariable})
  }

  updateFormNode(json: { reference_id: string }) {
    this.updateNode({...json, reference_type: ReferenceNodeType.Form})
  }

  updateWorkflowStepNode(json: { reference_id: string }) {
    this.updateNode({...json, reference_type: ReferenceNodeType.WorkflowStep})
  }

  updateWorkflowStepActionNode(json: { reference_id: string }) {
    this.updateNode({...json, reference_type: ReferenceNodeType.WorkflowStepAction})
  }

  /**
   * Update one of the four nodes in the path
   * Clear out the reference_id for the nodes further down the line since they won't be compatible anymore
   *
   * @param json
   * @private
   */
  private updateNode(json: { reference_id: string, reference_type: TakeActionReferenceNodeType }): void {
    const position = positionByReferenceType(json.reference_type)
    let node = this.path.nodes[position]
    if (node) {
      if (node.reference_id === json.reference_id) {
        return // nothing to update
      }
      node.reference_id = json.reference_id
      this.path.nodes.forEach((n, i) => {
        if (i > position) n.clearReferenceId()
      })
    } else {
      Logger.errorOnce('TakeActionStep', 'node not found.')
    }
  }

  get allReferencesLinked(): boolean {
    return this.variable_reference.allReferencesLinked
  }

  get path(): ITakeActionStepPath {
    return this.variable_reference?.source_variable_path
  }

  /**
   * Preemptively create the 4 nodes needed
   * @private
   */
  private initNodes(): void {
    const nodes: TakeActionReferenceNodes = [
      new ReferencePathNode(this.path, {reference_type: ReferenceNodeType.ProcessFlowVariable}),
      new ReferencePathNode(this.path, {reference_type: ReferenceNodeType.Form}),
      new ReferencePathNode(this.path, {reference_type: ReferenceNodeType.WorkflowStep}),
      new ReferencePathNode(this.path, {reference_type: ReferenceNodeType.WorkflowStepAction}),
    ] as TakeActionReferenceNodes
    this.path.initializeEmbeddedNodes(nodes)
  }
}

const positionByReferenceType = (referenceType: TakeActionReferenceNodeType): TakeActionNodePosition => {
  switch (referenceType) {
    case ReferenceNodeType.ProcessFlowVariable:
      return 0
    case ReferenceNodeType.Form:
      return 1
    case ReferenceNodeType.WorkflowStep:
      return 2
    case ReferenceNodeType.WorkflowStepAction:
      return 3
  }
}

